import { useState, useCallback, useEffect, useRef } from 'react';
import { validateCode } from '../../utils/registration/registrationHelpers.ts';
import { RESEND_CODE_COUNTDOWN } from '../../utils/registration/registrationConstants.ts';
import { fireAnalytics } from '../../utils/firebase.js';

interface UseCodeValidationParams {
  email: string;
  validateVerificationCode: (email: string, code: string) => Promise<any>;
  resendVerificationCode: (email: string) => Promise<any>;
  setStep: (step: 'email' | 'code' | 'password') => void;
  codeValidationLoading: boolean;
  resendCodeLoading: boolean;
  codeValidationError: string | null;
  resendCodeError: string | null;
}

export const useCodeValidation = ({
  email,
  validateVerificationCode,
  resendVerificationCode,
  setStep,
  codeValidationLoading,
  resendCodeLoading,
  codeValidationError,
  resendCodeError,
}: UseCodeValidationParams) => {
  const [code, setCode] = useState<string>('');
  const [codeError, setCodeError] = useState<string>('');
  const [countDown, setCountDown] = useState<number>(RESEND_CODE_COUNTDOWN);
  const [showResend, setShowResend] = useState<boolean>(false);
  const [pasteError, setPasteError] = useState<string>('');
  const handleSubmitRef = useRef<() => Promise<void>>();

  useEffect(() => {
    if (countDown > 0) {
      const timer = setTimeout(() => setCountDown(countDown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setShowResend(true);
    }
  }, [countDown]);

  const handleSubmit = useCallback(async () => {
    if (!validateCode(code)) {
      setCodeError('El código debe tener 6 dígitos');
      return;
    }

    setCodeError('');
    fireAnalytics("codigo_validacion_enviado", { category: "usuario" });

    try {
      await validateVerificationCode(email, code);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Código inválido. Intenta nuevamente.';
      setCodeError(errorMessage);
    }
  }, [code, email, validateVerificationCode]);

  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  useEffect(() => {
    if (code.length === 6 && validateCode(code)) {
      handleSubmitRef.current?.();
    }
  }, [code, handleSubmitRef]);

  useEffect(() => {
    if (codeValidationError) {
      setCodeError(codeValidationError);
    }
    if (resendCodeError) {
      setCodeError(resendCodeError);
    }
  }, [codeValidationError, resendCodeError]);

  const handleResend = useCallback(async () => {
    try {
      await resendVerificationCode(email);
      setCountDown(RESEND_CODE_COUNTDOWN);
      setShowResend(false);
      setCodeError('');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Error al reenviar el código. Intenta nuevamente.';
      setCodeError(errorMessage);
    }
  }, [email, resendVerificationCode]);

  const handleChangeEmail = useCallback(() => {
    setStep('email');
    setCode('');
    setCodeError('');
  }, [setStep]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").slice(0, 6);
    
    if (/^\d{6}$/.test(pastedData)) {
      setCode(pastedData);
      setPasteError("");
    }
  }, []);

  const handlePasteButton = useCallback(async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      const pastedData = clipboardText.slice(0, 6);
      
      if (/^\d{6}$/.test(pastedData)) {
        setCode(pastedData);
        setPasteError("");
        fireAnalytics("codigo_pegado_portapapeles", { category: "usuario" });
      } else {
        setPasteError("El contenido del portapapeles no es un código válido de 6 dígitos");
        setTimeout(() => setPasteError(""), 3000);
      }
    } catch (error) {
      setPasteError("No se pudo acceder al portapapeles. Intenta pegar manualmente (Ctrl+V)");
      setTimeout(() => setPasteError(""), 3000);
    }
  }, []);

  return {
    code,
    setCode,
    codeError,
    setCodeError,
    countDown,
    showResend,
    handleResend,
    handleChangeEmail,
    handleSubmit,
    pasteError,
    handlePaste,
    handlePasteButton,
    isLoading: codeValidationLoading || resendCodeLoading,
  };
};
